import { addPage, removePageAndChildren } from '../../services/pageservice';
import { mapTreeData, findNode, gatherKeysWithLevel } from '../../utils/treeUtils';
import { UseAddNodeConfig, AddNodeParams, TreeNode } from '../../types/types';
import { notification } from 'antd';

export const useTreeActions = ({ id, setLoading }: UseAddNodeConfig) => {
    const addNode = async ({
        selectedKey,
        newNodeTitle,
        data,
        setData,
        setNewNodeTitle,
        createdByGuid
    }: AddNodeParams) => {
        if (selectedKey && newNodeTitle) {
            setLoading(true);
            try {

                const responseData = await addPage(selectedKey, newNodeTitle, id, createdByGuid);
                const newNode = mapTreeData([responseData])[0];
                const newData = [...data];
               
                const addNodeRecursively = (nodes: TreeNode[]) => {
                    nodes.forEach((node: TreeNode) => {
                        if (node.key === selectedKey) {
                            node.children = node.children || [];
                            node.children.push(newNode);
                        } else if (node.children) {
                            addNodeRecursively(node.children);
                        }
                    });
                };
                addNodeRecursively(newData);
                setData(newData);
                setNewNodeTitle('');
            } catch (error) {
                console.error('There was an error adding the page!', error);
            } finally {
                setLoading(false);
            }
        }
    };

    const removeNode = async ({
        nodeKeyToRemove,
        nodeTitle,
        data,
        setData,
        setSelectedKey,
        setSelectedPageName,
        setSelectedView,
        getRootNodeKey
    }: {
        nodeKeyToRemove: string;
        nodeTitle: string;
        data: TreeNode[];
        setData: (data: TreeNode[]) => void;
        setSelectedKey: (key: string | null) => void;
        setSelectedPageName: (name: string) => void;
        setSelectedView: (view: 'editor' | 'pageview' | null) => void;
        getRootNodeKey: () => string | null;
    }) => {
        if (!nodeKeyToRemove) return;

        if (data.length > 0 && nodeKeyToRemove === data[0].key) {
            notification.error({
                message: 'Deletion Denied',
                description: 'Cannot delete the root page!',
                placement: 'bottom',
            });
            return;
        }

        setLoading(true);
        const nodeToRemove = findNode(data, nodeKeyToRemove) as TreeNode | null;

        if (!nodeToRemove) {
            setLoading(false);
            notification.warning({
                message: 'Node Not Found',
                description: 'Could not find the page to delete.',
                placement: 'bottom',
            });
            return;
        }

        const deleteRequests = gatherKeysWithLevel(nodeToRemove);
        const keysBeingRemoved = deleteRequests.map((req) => req.key);

        try {
            await removePageAndChildren(deleteRequests);

            const updatedData = JSON.parse(JSON.stringify(data));

            const removeRecursively = (nodes: TreeNode[], keysToRemove: string[]): TreeNode[] =>
                nodes
                    .filter((node) => !keysToRemove.includes(node.key))
                    .map((node) => ({
                        ...node,
                        children: node.children
                            ? removeRecursively(node.children, keysToRemove)
                            : undefined,
                    }))
                    .filter((node) => node);

            const newData = removeRecursively(updatedData, keysBeingRemoved);
            setData(newData);

            const newSelectedKeyCand = nodeToRemove.parentPageGuid || getRootNodeKey();
            setSelectedKey(newSelectedKeyCand);

            if (newSelectedKeyCand) {
                const newSelectedNode = findNode(newData, newSelectedKeyCand) as TreeNode | null;
                setSelectedPageName(newSelectedNode ? newSelectedNode.title : '');
                setSelectedView('pageview');
            } else {
                setSelectedPageName('');
                setSelectedView(null);
            }

            notification.success({
                message: 'Page Removed',
                description: `Page "${nodeTitle}" and its children removed.`,
                placement: 'bottom',
            });
        } catch (error: any) {
            console.error('Error removing page:', error);
            notification.error({
                message: 'Error Removing Page',
                description: error.response?.data?.message || 'An error occurred.',
                placement: 'bottom',
            });
        } finally {
            setLoading(false);
        }
    };

    return { addNode, removeNode };
};